import { Address } from './address';
import { Listing, ListingItem } from './listings';
import { LastMileShift, Shipment } from './shipping';
import { StoreBrief } from './stores';
import { OrderLedgerTransaction } from './transactions';

export enum ORDER_STATUS {
  zero = 0,
  requested = 1, // order has been requested and is waiting for seller to accept or reject
  accepted = 2, // order has been accepted by the seller and is currently being prepared
  rejected = 3, // order has been rejected by the seller ( due to stock, or other reasons )
  cancelled = 4, // order has been cancelled by the customer or management ( only before being shipped )
  delivered = 5, // order has been delivered to the customer
  returned = 6, // order has been returned to the store

  pending_payment = 7, // order is pending payment
  payment_failed = 8, // order payment has failed

  // ////// Shipping statuses (start at 20)
  ship__in_store__ready = 20, // order is ready for shipping in store
  ship__in_transit__to_silal = 21, // order has been picked up and is in transit from store to silal
  ship__at_silal__ready = 22, // order has arrived at silal and is ready for delivery to customer
  ship__in_transit__to_customer = 23, // order is in transit from silal/store to customer

  // Failure statuses
  ship__in_store__waiting_pickup_reschedule = 24, // order failed to be picked up from store, waiting for reschedule of pickup
  ship__in_transit__failed_dropoff_to_customer = 25, // order failed to be delivered to customer, still in transit ( with driver )
  ship__at_silal__waiting_dropoff_reschedule = 26, // order failed to be delivered to customer, currently at silal, waiting for reschedule of delivery

  // ////// Returning statuses (start at 50)
  return__with_customer__waiting_self_dropoff = 50, // order is with customer, waiting for customer to return it to silal
  return__with_customer__ready = 51, // order is with customer, ready for pickup by driver for return to silal
  return__in_transit__to_silal = 52, // order is in transit from customer to silal for return
  return__in_transit__to_store = 53, // order is in transit from silal to store for return
  return__at_silal__ready = 54, // order has arrived at silal, ready for return to store (scheduled drop off)
  return__at_silal__waiting_inspection = 55, // order is at silal, waiting for inspection before return approval to store

  // Failure statuses
  return__at_silal__return_rejected = 56, // order has arrived at silal, return has been rejected, waiting for customer to pickup
  return__at_silal__waiting_schedule = 57, // order is at silal, waiting for re/schedule of return to store
  return__in_transit__failed_dropoff_to_store = 58, // order failed to be returned to store, still in transit ( with driver )
}

export const ORDER_STATUS_GROUPS = {
  all: new Set([]),
  active: new Set([
    ORDER_STATUS.requested,
    ORDER_STATUS.accepted,
    ORDER_STATUS.pending_payment,
    ORDER_STATUS.ship__in_store__ready,
    ORDER_STATUS.ship__at_silal__ready,
    ORDER_STATUS.ship__in_transit__to_customer,
    ORDER_STATUS.ship__in_transit__to_silal,
    ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer,
    ORDER_STATUS.return__in_transit__failed_dropoff_to_store,
    ORDER_STATUS.return__with_customer__ready,
    ORDER_STATUS.return__in_transit__to_silal,
    ORDER_STATUS.return__in_transit__to_store,
    ORDER_STATUS.return__at_silal__ready,
    ORDER_STATUS.return__at_silal__waiting_inspection,
    ORDER_STATUS.return__at_silal__waiting_schedule,
    ORDER_STATUS.ship__in_store__waiting_pickup_reschedule,
    ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule,
  ]),
  requested: new Set([ORDER_STATUS.requested, ORDER_STATUS.pending_payment]),
  accepted: new Set([ORDER_STATUS.accepted]),
  ready_to_ship: new Set([
    ORDER_STATUS.ship__in_store__ready,
    ORDER_STATUS.ship__at_silal__ready,
  ]),
  shipped: new Set([
    ORDER_STATUS.ship__in_transit__to_customer,
    ORDER_STATUS.ship__in_transit__to_silal,
  ]),
  delivered: new Set([ORDER_STATUS.delivered]),
  cancelled_rejected: new Set([
    ORDER_STATUS.cancelled,
    ORDER_STATUS.rejected,
    ORDER_STATUS.payment_failed,
  ]),
  in_return: new Set([
    ORDER_STATUS.return__with_customer__waiting_self_dropoff,
    ORDER_STATUS.return__with_customer__ready,
    ORDER_STATUS.return__in_transit__to_silal,
    ORDER_STATUS.return__in_transit__to_store,
    ORDER_STATUS.return__at_silal__ready,
    ORDER_STATUS.return__at_silal__waiting_inspection,
  ]),
  returned: new Set([
    ORDER_STATUS.returned,
    ORDER_STATUS.return__at_silal__return_rejected,
  ]),
  with_captain: new Set([
    ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer,
    ORDER_STATUS.return__in_transit__failed_dropoff_to_store,
  ]),
  waiting_reschedule: new Set([
    ORDER_STATUS.return__at_silal__waiting_schedule,
    ORDER_STATUS.ship__in_store__waiting_pickup_reschedule,
    ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule,
  ]),
};

enum OrderDocumentType {
  TaxInvoiceAndReceipt,
  TaxInvoiceAndReceiptRefund,
  Receipt,
  ReceiptRefund,
  Quote,
  Order,
  SiteCustomerOrder,
  SiteCustomerOrderRefund,
  DeliveryNote,
  DeliveryNoteRefund,
  ProformaInvoice,
  DemandForPayment,
  DemandForPaymentRefund,
  TaxInvoice,
  TaxInvoiceRefund,
  ReceiptForTaxInvoice,
  ReceiptForTaxInvoiceRefund,
  DonationReceipt,
  DonationReceiptRefund,
}

enum OrderDocumentTypeVisuals {
  Green,
  Red,
  Neutral,
}

export function getOrderDocumentTypeVisualStyle(
  type: OrderDocumentType,
): OrderDocumentTypeVisuals {
  switch (type) {
    case OrderDocumentType.TaxInvoiceAndReceipt:
    case OrderDocumentType.Receipt:
    case OrderDocumentType.SiteCustomerOrder:
    case OrderDocumentType.ProformaInvoice:
    case OrderDocumentType.DemandForPayment:
    case OrderDocumentType.TaxInvoice:
    case OrderDocumentType.ReceiptForTaxInvoice:
    case OrderDocumentType.DonationReceipt:
      return OrderDocumentTypeVisuals.Green;
    case OrderDocumentType.TaxInvoiceAndReceiptRefund:
    case OrderDocumentType.ReceiptRefund:
    case OrderDocumentType.SiteCustomerOrderRefund:
    case OrderDocumentType.DeliveryNoteRefund:
    case OrderDocumentType.DemandForPaymentRefund:
    case OrderDocumentType.TaxInvoiceRefund:
    case OrderDocumentType.ReceiptForTaxInvoiceRefund:
    case OrderDocumentType.DonationReceiptRefund:
      return OrderDocumentTypeVisuals.Red;
    case OrderDocumentType.Quote:
    case OrderDocumentType.Order:
    case OrderDocumentType.DeliveryNote:
      return OrderDocumentTypeVisuals.Neutral;
  }
}

export const OrderDocumentTypeToIndexInServer: Record<
  OrderDocumentType,
  number
> = {
  [OrderDocumentType.TaxInvoiceAndReceipt]: 1,
  [OrderDocumentType.TaxInvoiceAndReceiptRefund]: 2,
  [OrderDocumentType.Receipt]: 3,
  [OrderDocumentType.ReceiptRefund]: 4,
  [OrderDocumentType.Quote]: 50,
  [OrderDocumentType.Order]: 100,
  [OrderDocumentType.SiteCustomerOrder]: 101,
  [OrderDocumentType.SiteCustomerOrderRefund]: 102,
  [OrderDocumentType.DeliveryNote]: 200,
  [OrderDocumentType.DeliveryNoteRefund]: 210,
  [OrderDocumentType.ProformaInvoice]: 300,
  [OrderDocumentType.DemandForPayment]: 301,
  [OrderDocumentType.DemandForPaymentRefund]: 302,
  [OrderDocumentType.TaxInvoice]: 305,
  [OrderDocumentType.TaxInvoiceRefund]: 330,
  [OrderDocumentType.ReceiptForTaxInvoice]: 400,
  [OrderDocumentType.ReceiptForTaxInvoiceRefund]: 410,
  [OrderDocumentType.DonationReceipt]: 405,
  [OrderDocumentType.DonationReceiptRefund]: 406,
};

export const OrderDocumentTypeName: Record<OrderDocumentType, string> = {
  [OrderDocumentType.TaxInvoiceAndReceipt]: 'Tax Invoice and Receipt',
  [OrderDocumentType.TaxInvoiceAndReceiptRefund]:
    'Tax Invoice and Receipt Refund',
  [OrderDocumentType.Receipt]: 'Receipt',
  [OrderDocumentType.ReceiptRefund]: 'Receipt Refund',
  [OrderDocumentType.Quote]: 'Quote',
  [OrderDocumentType.Order]: 'Order',
  [OrderDocumentType.SiteCustomerOrder]: 'Site Customer Order',
  [OrderDocumentType.SiteCustomerOrderRefund]: 'Site Customer Order Refund',
  [OrderDocumentType.DeliveryNote]: 'Delivery Note',
  [OrderDocumentType.DeliveryNoteRefund]: 'Delivery Note Refund',
  [OrderDocumentType.ProformaInvoice]: 'Proforma Invoice',
  [OrderDocumentType.DemandForPayment]: 'Demand for Payment',
  [OrderDocumentType.DemandForPaymentRefund]: 'Demand for Payment Refund',
  [OrderDocumentType.TaxInvoice]: 'Tax Invoice',
  [OrderDocumentType.TaxInvoiceRefund]: 'Tax Invoice Refund',
  [OrderDocumentType.ReceiptForTaxInvoice]: 'Receipt for Tax Invoice',
  [OrderDocumentType.ReceiptForTaxInvoiceRefund]:
    'Receipt for Tax Invoice Refund',
  [OrderDocumentType.DonationReceipt]: 'Donation Receipt',
  [OrderDocumentType.DonationReceiptRefund]: 'Donation Receipt Refund',
};

export const ORDER_STATUS_NAME_MAP: {
  [key in ORDER_STATUS]: string;
} = {
  [ORDER_STATUS.zero]: 'Zero',
  [ORDER_STATUS.requested]: 'Requested',
  [ORDER_STATUS.accepted]: 'Accepted',
  [ORDER_STATUS.rejected]: 'Rejected',
  [ORDER_STATUS.cancelled]: 'Cancelled',
  [ORDER_STATUS.delivered]: 'Delivered',
  [ORDER_STATUS.returned]: 'Returned',
  [ORDER_STATUS.pending_payment]: 'Pending Payment',
  [ORDER_STATUS.payment_failed]: 'Payment Failed',

  // Shipping statuses
  [ORDER_STATUS.ship__in_store__ready]: 'Ready for Pickup (With Store)',
  [ORDER_STATUS.ship__in_transit__to_silal]: 'In Transit (Store to Silal)',
  [ORDER_STATUS.ship__at_silal__ready]: 'Ready for Delivery (With Silal)',
  [ORDER_STATUS.ship__in_transit__to_customer]: 'In Transit (To Customer)',

  // Failure statuses
  [ORDER_STATUS.ship__in_store__waiting_pickup_reschedule]:
    'Pickup Reschedule Needed',
  [ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer]:
    'Failed Dropoff to Customer',
  [ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule]:
    'Delivery Reschedule Needed',

  // Return statuses
  [ORDER_STATUS.return__with_customer__waiting_self_dropoff]:
    'Awaiting Customer Return (With Customer)',
  [ORDER_STATUS.return__with_customer__ready]:
    'Ready for Pickup (With Customer)',
  [ORDER_STATUS.return__in_transit__to_silal]: 'In Transit (Customer to Silal)',
  [ORDER_STATUS.return__in_transit__to_store]: 'In Transit (Silal to Store)',
  [ORDER_STATUS.return__at_silal__ready]: 'Ready for Return (Silal to Store)',
  [ORDER_STATUS.return__at_silal__waiting_inspection]:
    'Awaiting Inspection (Silal)',

  // Failure statuses
  [ORDER_STATUS.return__at_silal__return_rejected]: 'Return Rejected (Silal)',
  [ORDER_STATUS.return__at_silal__waiting_schedule]:
    'Reschedule Return Needed (Silal)',
  [ORDER_STATUS.return__in_transit__failed_dropoff_to_store]:
    'Return Failed (Silal to Store)',
};

export const ORDER_STATUS_COLOR_MAP: {
  [key in ORDER_STATUS]: string;
} = {
  [ORDER_STATUS.zero]: '#000000', // Placeholder color
  [ORDER_STATUS.requested]: 'gray', // Amethyst Purple
  [ORDER_STATUS.accepted]: 'lime', // Emerald Green
  [ORDER_STATUS.rejected]: 'volcano', // Alizarin Red
  [ORDER_STATUS.cancelled]: 'orange', // Sunflower Yellow
  [ORDER_STATUS.delivered]: 'green', // Peter River Blue
  [ORDER_STATUS.returned]: 'magenta', // Amethyst Purple
  [ORDER_STATUS.pending_payment]: 'orange', // Sunflower Yellow
  [ORDER_STATUS.payment_failed]: 'red', // Alizarin Red
  [ORDER_STATUS.ship__in_store__ready]: 'cyan', // Nephritis Green
  [ORDER_STATUS.ship__in_transit__to_silal]: 'geekblue', // Carrot Orange
  [ORDER_STATUS.ship__at_silal__ready]: 'cyan', // Turquoise Green
  [ORDER_STATUS.ship__in_transit__to_customer]: 'geekblue', // Peter River Blue
  [ORDER_STATUS.ship__in_store__waiting_pickup_reschedule]: 'purple', // Sunflower Yellow
  [ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer]: 'orange', // Alizarin Red
  [ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule]: 'purple', // Carrot Orange
  [ORDER_STATUS.return__with_customer__waiting_self_dropoff]: 'gold', // Sunflower Yellow
  [ORDER_STATUS.return__with_customer__ready]: 'cyan', // Emerald Green
  [ORDER_STATUS.return__in_transit__to_silal]: 'geekblue', // Peter River Blue
  [ORDER_STATUS.return__in_transit__to_store]: 'geekblue', // Alizarin Red
  [ORDER_STATUS.return__at_silal__ready]: 'cyan', // Nephritis Green
  [ORDER_STATUS.return__at_silal__waiting_inspection]: 'volcano', // Amethyst Purple
  [ORDER_STATUS.return__at_silal__return_rejected]: 'red', // Carrot Orange
  [ORDER_STATUS.return__at_silal__waiting_schedule]: 'purple', // Sunflower Yellow
  [ORDER_STATUS.return__in_transit__failed_dropoff_to_store]: 'orange', // Alizarin Red
};

export const terminalStatuses = new Set([
  ORDER_STATUS.cancelled,
  ORDER_STATUS.rejected,
  ORDER_STATUS.delivered,
  ORDER_STATUS.returned,
  ORDER_STATUS.payment_failed,
]);

export const DUMMY_ORDER_ACTIONS: OrderAction[] = [
  {
    order_id: 1,
    action: ORDER_STATUS.zero,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.zero], // Placeholder for zero state
    action_date: '2024-05-28T00:00:00.000Z', // Today's date in UTC
  },
  {
    order_id: 1,
    action: ORDER_STATUS.requested,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.requested],
    action_date: '2024-05-28T09:00:00.000Z', // Example time after order placement
  },
  {
    order_id: 1,
    action: ORDER_STATUS.accepted,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.accepted],
    action_date: '2024-05-28T10:00:00.000Z', // Example time after requested
  },
  {
    order_id: 1,
    action: ORDER_STATUS.rejected,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.rejected],
    action_date: '2024-05-28T11:00:00.000Z', // Example time after accepted
  },
  {
    order_id: 1,
    action: ORDER_STATUS.cancelled,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.cancelled],
    action_date: '2024-05-28T12:00:00.000Z', // Example time after accepted/requested
  },
  {
    order_id: 1,
    action: ORDER_STATUS.delivered,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.delivered],
    action_date: '2024-05-29T10:00:00.000Z', // Example time after in-transit
  },
  {
    order_id: 1,
    action: ORDER_STATUS.returned,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.returned],
    action_date: '2024-05-29T12:00:00.000Z', // Example time after delivered
  },

  // Shipping statuses
  {
    order_id: 1,
    action: ORDER_STATUS.ship__in_store__ready,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.ship__in_store__ready],
    action_date: '2024-05-28T13:00:00.000Z', // Example time after accepted
  },
  {
    order_id: 1,
    action: ORDER_STATUS.ship__in_transit__to_silal,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.ship__in_transit__to_silal],
    action_date: '2024-05-28T14:00:00.000Z', // Example time after ready for pickup
  },
  {
    order_id: 1,
    action: ORDER_STATUS.ship__at_silal__ready,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.ship__at_silal__ready],
    action_date: '2024-05-28T15:00:00.000Z', // Example time after in-transit
  },
  {
    order_id: 1,
    action: ORDER_STATUS.ship__in_transit__to_customer,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.ship__in_transit__to_customer],
    action_date: '2024-05-28T16:00:00.000Z', // Example time after ready for delivery
  },

  // Failure statuses (For Shipping)
  {
    order_id: 1,
    action: ORDER_STATUS.ship__in_store__waiting_pickup_reschedule,
    action_name:
      ORDER_STATUS_NAME_MAP[
        ORDER_STATUS.ship__in_store__waiting_pickup_reschedule
      ],
    action_date: '2024-05-28T17:00:00.000Z', // Example time after ready for pickup
  },
  {
    order_id: 1,
    action: ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer,
    action_name:
      ORDER_STATUS_NAME_MAP[
        ORDER_STATUS.ship__in_transit__failed_dropoff_to_customer
      ],
    action_date: '2024-05-28T18:00:00.000Z', // Example time after in-transit
  },
  {
    order_id: 1,
    action: ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule,
    action_name:
      ORDER_STATUS_NAME_MAP[
        ORDER_STATUS.ship__at_silal__waiting_dropoff_reschedule
      ],
    action_date: '2024-05-29T09:00:00.000Z', // Example time after ready for delivery
  },

  // Return statuses
  {
    order_id: 1,
    action: ORDER_STATUS.return__with_customer__waiting_self_dropoff,
    action_name:
      ORDER_STATUS_NAME_MAP[
        ORDER_STATUS.return__with_customer__waiting_self_dropoff
      ],
    action_date: '2024-05-29T13:00:00.000Z', // Example time after returned
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__with_customer__ready,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__with_customer__ready],
    action_date: '2024-05-29T14:00:00.000Z', // Example time after awaiting self dropoff
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__in_transit__to_silal,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__in_transit__to_silal],
    action_date: '2024-05-29T15:00:00.000Z', // Example time after ready for pickup
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__in_transit__to_store,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__in_transit__to_store],
    action_date: '2024-05-29T16:00:00.000Z', // Example time after in-transit to silal
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__at_silal__ready,
    action_name: ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__at_silal__ready],
    action_date: '2024-05-29T17:00:00.000Z', // Example time after in-transit to store
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__at_silal__waiting_inspection,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__at_silal__waiting_inspection],
    action_date: '2024-05-30T09:00:00.000Z', // Example time after ready for return
  },

  // Failure statuses (For Return)
  {
    order_id: 1,
    action: ORDER_STATUS.return__at_silal__return_rejected,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__at_silal__return_rejected],
    action_date: '2024-05-30T10:00:00.000Z', // Example time after waiting inspection
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__at_silal__waiting_schedule,
    action_name:
      ORDER_STATUS_NAME_MAP[ORDER_STATUS.return__at_silal__waiting_schedule],
    action_date: '2024-05-30T11:00:00.000Z', // Example time after waiting inspection
  },
  {
    order_id: 1,
    action: ORDER_STATUS.return__in_transit__failed_dropoff_to_store,
    action_name:
      ORDER_STATUS_NAME_MAP[
        ORDER_STATUS.return__in_transit__failed_dropoff_to_store
      ],
    action_date: '2024-05-30T12:00:00.000Z', // Example time after in-transit to store
  },
];

export interface AbstractOrder {
  id: number;
  customer_id: number;
  store_id: number;
  payment_type: number;
  placing_date: string;
  status: ORDER_STATUS;
  last_mile_shift_id: number;
  order_actions: OrderAction[];
  delivery_fee: string;
  vat: string;
  discounts_total: string;
  items_total: string;
  customer_pays: string;
  silal_fees_total: string;
  store_recieves: string;
}

export interface OrderBrief extends AbstractOrder {
  source_address: Address;
  destination_address: Address;
  items: OrderItemBrief[];
  shipments: Shipment[];
  last_mile_shift: LastMileShift;
}

export interface OrderDetails extends AbstractOrder {
  source_address: Address;
  destination_address: Address;
  items: OrderItemDetailed[];
  store_name_en: string;
  store_name_ar: string;
  store: StoreBrief;
  shipments: Shipment[];
  last_mile_shift: LastMileShift;
  order_ledger: OrderLedger;
  cash_to_collect: number;
  security_code?: string;
  management_notes?: string;
  discount_lines: DiscountLine[];
  order_documents?: OrderDocument[][];
}

export interface OrderAction {
  order_id: number;
  action: ORDER_STATUS;
  action_name: string;
  action_date: string;
}

export interface OrderItemBrief {
  id: number;
  unit_price: string;
  quantity: number;
  seller_notes?: any;
  order_id: number;
  item: ListingItem;
  silal_fee: string;
}

export interface OrderItemDetailed {
  id: number;
  unit_price: string;
  quantity: number;
  seller_notes?: any;
  order_id: number;
  item: ListingItem;
  silal_fee: string;
  listing: Listing;
}

export interface OrderLedger {
  order_id: number;
  amount_paid_by_customer_in_cash: number;
  amount_paid_by_customer_in_credit_card: number;
  amount_paid_by_customer_in_credit_balance: number;
  amount_paid_by_customer: number;
  amount_refunded_in_credit_card: number;
  amount_refunded_in_credit_balance: number;
  amount_refunded: number;
  debt_to_store_pre_deductions: number;
  pending_debt_to_store_balance: number;
  amount_deducted_from_store_debt: number;
  net_debt_to_store: number;
  transactions_json: OrderLedgerTransaction[];
}

export interface OrderDocument {
  document_id: number;
  order_id: number;
  shipment_id: number;
  document_type: OrderDocumentType;
  document_type_name: string;
  date_created: string;
  viewed: boolean;
}

export interface DiscountLine {
  id: number;
  discount_type: string;
  amount: string;
  amount_deducted_from_store: string;
  name_en: string;
  name_ar: string;
  is_delivery_discount: boolean;
  item_id?: number;
  promotion_id: number;
}
